import React, { useState, useEffect } from 'react';
import styles from './InvoiceDetail.module.css';
import { invoiceHeaderApi } from '../../../../../api/invoiceHeader/invoiceHeaderApi';

const InvoiceDetail = ({ initialInvoiceNum, onBack }) => {
  const [invoiceNum, setInvoiceNum] = useState(initialInvoiceNum || '');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialInvoiceNum) {
      setInvoiceNum(initialInvoiceNum);
    }
  }, [initialInvoiceNum]);

  useEffect(() => {
    if (invoiceNum) {
      fetchInvoiceDetails();
    }
  }, [invoiceNum]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoiceHeaderApi.getInvoiceDetails(invoiceNum);
      setInvoice(data);
    } catch {
      setError('Failed to load invoice details');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'USD' ? '$' : '₹';
    return symbol + (amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeValue) => {
    if (!timeValue) return 'N/A';
    try {
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* Back button */}
      <div className={styles.backRow}>
        <button onClick={onBack} className={styles.backButton}>
          ← Back to Invoice Headers
        </button>
        {invoice && (
          <span className={styles.invoiceTitle}>Invoice #{invoice.invoiceNum}</span>
        )}
      </div>

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading invoice details...</div>
        </div>
      )}

      {error && <div className={styles.errorMessage}>{error}</div>}

      {invoice && !loading && (
        <>
          {/* Invoice Information */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Invoice Information</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>Invoice #</label>
                <span className={styles.value}>{invoice.invoiceNum}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Invoice Date</label>
                <span className={styles.value}>{formatDate(invoice.invoiceDate)}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Invoice Time</label>
                <span className={styles.value}>{formatTime(invoice.invoiceTime)}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Status</label>
                <span className={`${styles.statusBadge} ${styles[`status${invoice.status?.replace(/\s+/g, '')}`]}`}>
                  {invoice.status}
                </span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Due Date</label>
                <span className={styles.value}>{formatDate(invoice.dueDate)}</span>
              </div>
            </div>
          </div>

          {/* Sales Order Information */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Sales Order Information</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>SO #</label>
                <span className={styles.value}>{invoice.soNum || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>SO Date</label>
                <span className={styles.value}>{formatDate(invoice.soDate)}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Order Time</label>
                <span className={styles.value}>{formatTime(invoice.orderTime)}</span>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Customer Information</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>Customer Name</label>
                <span className={styles.value}>{invoice.customerName}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Contact Person</label>
                <span className={styles.value}>{invoice.contactPersonName || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Sales Person</label>
                <span className={styles.value}>{invoice.salesPerson || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Financial Details */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Financial Details</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>Total Amount</label>
                <span className={styles.totalAmount}>{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Currency</label>
                <span className={styles.value}>{invoice.currency}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Total Lines</label>
                <span className={styles.value}>{invoice.totalLines}</span>
              </div>
            </div>
          </div>

          {/* Dispatch Information */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Dispatch Information</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label}>Dispatch Date</label>
                <span className={styles.value}>{formatDate(invoice.dispatchDate)}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Tracking #</label>
                <span className={styles.value}>{invoice.trackingNum || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Transport</label>
                <span className={styles.value}>{invoice.transport || 'N/A'}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {!invoice && !loading && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🧾</div>
          <div className={styles.emptyText}>No invoice selected</div>
          <button onClick={onBack} className={styles.backButton} style={{ marginTop: 16 }}>
            ← Go to Invoice Headers
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;